/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  Box,
  Divider,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import React from 'react';
import type { NumberWithUnitValue } from '@opengeoweb/shared';
import {
  dateUtils,
  DateTimePicker,
  NumberWithUnitField,
} from '@opengeoweb/shared';
import { CalendarToday } from '@opengeoweb/theme';
import { AnchorTimeField } from './AnchorTimeField';
import type {
  AnimationLengthSetting,
  OffsetSelections,
  RelativeTimeOffset,
  TimeUnit,
} from './animationLengthUtils';
import {
  activeOffsets,
  AnimationLengthMode,
  clampOffset,
  defaultExactSelections,
  defaultOffsetSelections,
  defaultRecurringPeriodSelections,
  isAnchoredMode,
  isRecurringPeriodMode,
  isValidAnimationLength,
  isWithinAnimationStepLimit,
  maxOffsetByUnit,
  modeTranslationKey,
  timeUnits,
  toExactAnimationTime,
  unitTranslationKey,
} from './animationLengthUtils';
import { useTimesliderTranslation } from '../../../utils/i18n';
import { minutesToDescribedDuration } from '../../TimeSlider/timeSliderUtils';

export interface AnimationLengthMenuProps {
  setting: AnimationLengthSetting;
  onChange: (setting: AnimationLengthSetting, isStep?: boolean) => void;
  onInputChange?: () => void;
  animationStartTime?: string;
  animationEndTime?: string;
  autoTimeStepLayerName?: string;
  timeStep?: number;
  isAutoDisabled?: boolean;
  disabled?: boolean;
  referenceTimeFromLayer?: string;
}

const selectableModes = [
  AnimationLengthMode.Auto,
  AnimationLengthMode.Now,
  AnimationLengthMode.RecurringPeriod,
  AnimationLengthMode.ReferenceTime,
  AnimationLengthMode.Exact,
];

const OFFSET_MAX_DIGITS = 3;

/* One line of MuiFormHelperText: 20px line box plus its 3px top margin. */
const RESERVED_ERROR_HEIGHT = '23px';

/* Every row splits the panel the same way, so the fields line up in two columns. */
const fieldRow = {
  display: 'flex',
  gap: 1,
  '& > *': { flex: '1 1 0' },
};

const styles = {
  menu: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: '260px',
    padding: '8px 12px',
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 700,
    paddingBottom: 2,
  },
  caption: {
    color: 'geowebColors.greys.accessible',
    fontSize: 11,
    paddingLeft: 0.5,
  },
  radioLabel: {
    '& .MuiFormControlLabel-label': { fontSize: 14 },
  },
  anchorRow: {
    alignItems: 'center',
    display: 'flex',
    gap: 1,
    paddingLeft: 0.5,
  },
  anchorValue: {
    flexGrow: 1,
    fontSize: 14,
    fontWeight: 700,
  },
  anchorFields: fieldRow,
  /* The top padding matches the reserved error line below, so the fields sit between equal gaps. */
  offsets: {
    ...fieldRow,
    paddingTop: RESERVED_ERROR_HEIGHT,
  },
  reservedError: {
    visibility: 'hidden',
  },
  readOnlyValue: {
    flexGrow: 1,
    fontSize: 14,
  },
  exact: {
    display: 'flex',
    flexDirection: 'column',
  },
  dateTimePicker: {
    paddingBottom: 2,
  },
  utc: {
    color: 'geowebColors.greys.accessible',
    fontSize: 18,
  },
};

export const AnimationLengthMenu: React.FC<AnimationLengthMenuProps> = ({
  setting,
  onChange,
  onInputChange,
  animationStartTime,
  animationEndTime,
  autoTimeStepLayerName,
  timeStep,
  isAutoDisabled = false,
  disabled = false,
  referenceTimeFromLayer,
}) => {
  const { t } = useTimesliderTranslation();
  const modeGroupLabelId = React.useId();

  /* Spreading the setting keeps every other mode's selections while this one changes. */
  const changeMode = (mode: AnimationLengthMode): void => {
    if (isRecurringPeriodMode(mode)) {
      onChange({
        ...setting,
        mode,
        recurringPeriod:
          setting.recurringPeriod ?? defaultRecurringPeriodSelections(),
      });
      return;
    }
    if (mode === AnimationLengthMode.Now) {
      onChange({
        ...setting,
        mode,
        now: setting.now ?? defaultOffsetSelections,
      });
      return;
    }
    if (mode === AnimationLengthMode.Exact) {
      onChange({
        ...setting,
        mode,
        exact: setting.exact ?? defaultExactSelections(),
      });
      return;
    }
    if (mode === AnimationLengthMode.ReferenceTime) {
      onChange({
        ...setting,
        mode,
        referenceTime: setting.referenceTime ?? defaultOffsetSelections,
      });
      return;
    }
    onChange({ ...setting, mode });
  };

  const { startOffset, endOffset } = activeOffsets(setting);

  const changeActiveOffsets = (
    offsets: OffsetSelections,
    isStep?: boolean,
  ): void => {
    if (setting.mode === AnimationLengthMode.RecurringPeriod) {
      onChange(
        {
          ...setting,
          recurringPeriod: { ...setting.recurringPeriod, ...offsets },
        },
        isStep,
      );
      return;
    }
    if (setting.mode === AnimationLengthMode.Now) {
      onChange({ ...setting, now: offsets }, isStep);
    }
    if (setting.mode === AnimationLengthMode.ReferenceTime) {
      onChange({ ...setting, referenceTime: offsets }, isStep);
    }
  };

  const changeAnchorTime = (changedAnchorTime: string): void => {
    if (setting.mode !== AnimationLengthMode.RecurringPeriod) {
      return;
    }
    onChange({
      ...setting,
      recurringPeriod: {
        ...setting.recurringPeriod,
        anchorTime: changedAnchorTime,
      },
    });
  };

  const unitOptions = timeUnits.map((unit) => ({
    value: unit,
    label: t(unitTranslationKey[unit]),
  }));

  const asTimeOffset = ({
    value,
    unit,
  }: NumberWithUnitValue): RelativeTimeOffset =>
    clampOffset({ value, unit: unit as TimeUnit });

  /* One unit spans the whole window, so picking it on either field moves both. */
  const changeStartOffset = (
    offset: NumberWithUnitValue,
    isStep?: boolean,
  ): void => {
    const changed = asTimeOffset(offset);
    changeActiveOffsets(
      {
        startOffset: changed,
        endOffset: asTimeOffset({ ...endOffset, unit: changed.unit }),
      },
      isStep,
    );
  };

  const changeEndOffset = (
    offset: NumberWithUnitValue,
    isStep?: boolean,
  ): void => {
    const changed = asTimeOffset(offset);
    changeActiveOffsets(
      {
        startOffset: asTimeOffset({ ...startOffset, unit: changed.unit }),
        endOffset: changed,
      },
      isStep,
    );
  };

  const resolvesOwnRange = isAnchoredMode(setting.mode);
  const hasInvertedRange = !isValidAnimationLength(setting, {
    referenceTime: referenceTimeFromLayer,
  });
  const hasTooManySteps =
    resolvesOwnRange &&
    !hasInvertedRange &&
    !isWithinAnimationStepLimit(setting, timeStep);

  const hasRangeError = hasInvertedRange || hasTooManySteps;

  const rangeError = ((): string => {
    if (hasInvertedRange) {
      return t('timeslider-start-before-end');
    }
    if (hasTooManySteps) {
      return t('timeslider-animation-too-many-steps');
    }
    return ' ';
  })();

  const isModeDisabled = (mode: AnimationLengthMode): boolean =>
    disabled ||
    (mode === AnimationLengthMode.Auto && isAutoDisabled) ||
    (mode === AnimationLengthMode.ReferenceTime && !referenceTimeFromLayer);

  const describedAnimationLength =
    animationStartTime && animationEndTime
      ? minutesToDescribedDuration(
          dateUtils.differenceInMinutes(
            dateUtils.utc(animationEndTime),
            dateUtils.utc(animationStartTime),
          ),
        )
      : '';

  const asDisplayTime = (time?: string): string =>
    time
      ? `${dateUtils.dateToString(dateUtils.utc(time), dateUtils.DATE_FORMAT_DATEPICKER, true)}`
      : '';

  const isSteppingExactTimeRef = React.useRef(false);

  const handleExactTimeKeyDown = (event: React.KeyboardEvent): void => {
    isSteppingExactTimeRef.current =
      event.key === 'ArrowUp' || event.key === 'ArrowDown';
  };

  /* Typing walks through incomplete dates, so only a whole one is worth reporting. */
  const changeExactStartTime = (date: Date | null): void => {
    if (setting.mode !== AnimationLengthMode.Exact || !date) {
      return;
    }
    const isStep = isSteppingExactTimeRef.current;
    isSteppingExactTimeRef.current = false;
    onChange(
      {
        ...setting,
        exact: {
          ...setting.exact,
          animationStartTime: toExactAnimationTime(date),
        },
      },
      isStep,
    );
  };

  const changeExactEndTime = (date: Date | null): void => {
    if (setting.mode !== AnimationLengthMode.Exact || !date) {
      return;
    }
    const isStep = isSteppingExactTimeRef.current;
    isSteppingExactTimeRef.current = false;
    onChange(
      {
        ...setting,
        exact: {
          ...setting.exact,
          animationEndTime: toExactAnimationTime(date),
        },
      },
      isStep,
    );
  };

  return (
    <Box sx={styles.menu}>
      <Typography id={modeGroupLabelId} sx={styles.sectionTitle}>
        {t('timeslider-animation-length')}
      </Typography>
      <RadioGroup
        aria-labelledby={modeGroupLabelId}
        onChange={(event): void =>
          changeMode(event.target.value as AnimationLengthMode)
        }
        value={setting.mode}
      >
        {selectableModes.map((mode) => (
          <FormControlLabel
            control={<Radio size="small" />}
            disabled={isModeDisabled(mode)}
            key={mode}
            label={t(modeTranslationKey[mode])}
            sx={styles.radioLabel}
            value={mode}
          />
        ))}
      </RadioGroup>

      <Divider sx={{ marginY: 1 }} />

      {setting.mode === AnimationLengthMode.Exact ? (
        <Typography sx={styles.sectionTitle}>
          {t('timeslider-start-and-end-time')}
        </Typography>
      ) : (
        <Typography sx={styles.sectionTitle}>
          {t('timeslider-anchor-time')}
        </Typography>
      )}

      {setting.mode === AnimationLengthMode.Auto && (
        <Box>
          <Typography sx={styles.readOnlyValue}>
            {autoTimeStepLayerName}
          </Typography>
          <Typography sx={styles.caption}>
            {t('timeslider-auto-animation-length')}
          </Typography>
          <Typography sx={{ ...styles.readOnlyValue, paddingLeft: 0.5 }}>
            {describedAnimationLength}
          </Typography>
        </Box>
      )}

      {resolvesOwnRange ? (
        <Box>
          {setting.mode === AnimationLengthMode.Now && (
            <>
              <Typography sx={styles.caption}>
                {t('timeslider-date')}
              </Typography>
              <Box sx={styles.anchorRow}>
                <CalendarToday />
                <Typography sx={styles.anchorValue}>
                  {t('timeslider-now')}
                </Typography>
                <Typography sx={styles.utc}>UTC</Typography>
              </Box>
            </>
          )}

          {setting.mode === AnimationLengthMode.RecurringPeriod && (
            <Box sx={styles.anchorFields}>
              <AnchorTimeField
                disabled={disabled}
                label={t('timeslider-time')}
                onChange={changeAnchorTime}
                value={setting.recurringPeriod.anchorTime}
              />
              <Box>
                <Typography sx={styles.caption}>
                  {t('timeslider-date')}
                </Typography>
                <Box sx={styles.anchorRow}>
                  <CalendarToday />
                  <Typography sx={styles.anchorValue}>
                    {t('timeslider-today')}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {setting.mode === AnimationLengthMode.ReferenceTime && (
            <>
              <Typography sx={styles.caption}>
                {t('timeslider-reference-time')}
              </Typography>
              <Box sx={styles.anchorRow}>
                <CalendarToday />
                <Typography sx={styles.readOnlyValue}>
                  {asDisplayTime(referenceTimeFromLayer)}
                </Typography>
                <Typography sx={styles.utc}>UTC</Typography>
              </Box>
            </>
          )}

          <Box sx={styles.offsets}>
            <NumberWithUnitField
              disabled={disabled}
              error={hasRangeError}
              label={t('timeslider-start-time')}
              maxDigits={OFFSET_MAX_DIGITS}
              maxMagnitude={maxOffsetByUnit[startOffset.unit]}
              onChange={changeStartOffset}
              onInputChange={onInputChange}
              unit={startOffset.unit}
              unitOptions={unitOptions}
              value={startOffset.value}
            />
            <NumberWithUnitField
              disabled={disabled}
              error={hasRangeError}
              label={t('timeslider-end-time')}
              maxDigits={OFFSET_MAX_DIGITS}
              maxMagnitude={maxOffsetByUnit[endOffset.unit]}
              onChange={changeEndOffset}
              onInputChange={onInputChange}
              unit={endOffset.unit}
              unitOptions={unitOptions}
              value={endOffset.value}
            />
          </Box>
        </Box>
      ) : null}

      {setting.mode === AnimationLengthMode.Exact && (
        <Box sx={styles.exact}>
          <DateTimePicker
            label={t('timeslider-start-time')}
            onChange={changeExactStartTime}
            slotProps={{
              textField: {
                error: hasRangeError,
                onKeyDown: handleExactTimeKeyDown,
              },
            }}
            sx={styles.dateTimePicker}
            value={new Date(setting.exact.animationStartTime)}
          />
          <DateTimePicker
            label={t('timeslider-end-time')}
            onChange={changeExactEndTime}
            slotProps={{
              textField: {
                error: hasRangeError,
                onKeyDown: handleExactTimeKeyDown,
              },
            }}
            value={new Date(setting.exact.animationEndTime)}
          />
        </Box>
      )}
      {/* Always rendered so showing the message cannot resize the panel. */}
      <FormHelperText
        data-testid="animationLengthRangeError"
        error
        sx={hasRangeError ? undefined : styles.reservedError}
      >
        {rangeError}
      </FormHelperText>
    </Box>
  );
};
